import { computed, toValue } from 'vue';

import { useYardAssetRowsPresenter } from '@satellite/composables/useYardAssetRowsPresenter';

import type { MaybeRefOrGetter, ComputedRef } from 'vue';
import type { YardAssetRow } from '@satellite/composables/useYardAssetRowsPresenter';

interface IProcessedAsset {
  assetId: string;
  assetCode: string;
  icon: string;
  isAssigned: boolean;
  spotAreaName: string | null;
  spotId: string | null;
  spotCode: string | null;
  disabled: boolean;
}

/**
 * Options for useYardAssetSelect composable.
 *
 * `warehouseId` wants a getter, not a value: a Pinia store is `reactive`, so
 * `store.selectedWarehouseId` reads as a plain string and passing it directly would pin the picker
 * to whatever warehouse was selected when the dialog mounted — and these dialogs never unmount.
 */
interface IUseYardAssetSelectOptions {
  warehouseId?: MaybeRefOrGetter<string | null>;
  includeWaitingAssignmentAssets?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssignedAssets?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssociatedToActiveMoveTasks?: MaybeRefOrGetter<boolean | 'disabled'>;
  search?: MaybeRefOrGetter<string>;
  showDisabled?: MaybeRefOrGetter<boolean>;
}

interface IYardAssetSelect {
  assetsRef: ComputedRef<IProcessedAsset[]>;
  isLoadingRef: ComputedRef<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Composable for asset selection functionality.
 *
 * Rows come from the `yard/view/assets` BFF, which already answers everything the picker needs per
 * container: code, spot, area and the open move tasks holding it. Filtering and search stay
 * client-side because the endpoint returns the whole option set.
 */
export function useYardAssetSelect(options: IUseYardAssetSelectOptions = {}): IYardAssetSelect {
  const {
    warehouseId,
    includeWaitingAssignmentAssets = false,
    includeAssignedAssets = false,
    includeAssociatedToActiveMoveTasks = false,
    search = '',
    showDisabled = true
  } = options;

  // `headless`: the dialog is mounted for the whole authenticated session (see `App.vue`), so there
  // is no viewport to gate the fetch on.
  const {
    data: assetRows,
    isLoading,
    refresh
  } = useYardAssetRowsPresenter({
    warehouseId: computed(() => toValue(warehouseId) || null),
    headless: true
  });

  /**
   * Check if an asset container is assigned to a spot.
   */
  const isAssetAssigned = (asset: YardAssetRow): boolean => Boolean(asset.spot?.id);

  /**
   * Check if an asset container is held by an open move task.
   *
   * Resolved per container by the BFF, not per spot: a move task merely *targeting* the spot an
   * asset happens to sit on does not lock that asset.
   */
  const isAssetInActiveMoveTask = (asset: YardAssetRow): boolean =>
    (asset.openMoveTasks?.length ?? 0) > 0;

  /**
   * Check if asset should be excluded based on filters
   */
  const shouldExcludeWaitingAssignmentAsset = (asset: YardAssetRow): boolean => {
    return !isAssetAssigned(asset);
  };

  const shouldExcludeAssignedAsset = (asset: YardAssetRow): boolean => {
    return isAssetAssigned(asset);
  };

  const shouldExcludeAssetAssociatedToActiveMoveTask = (asset: YardAssetRow): boolean => {
    return isAssetInActiveMoveTask(asset);
  };

  /**
   * Check if asset should be disabled (included but not selectable)
   */
  const shouldDisableAsset = (asset: YardAssetRow): boolean => {
    const includeWaitingValue = toValue(includeWaitingAssignmentAssets);
    const includeAssignedValue = toValue(includeAssignedAssets);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const includeWaitingDisabled = includeWaitingValue === 'disabled' && !isAssetAssigned(asset);
    const includeAssignedDisabled = includeAssignedValue === 'disabled' && isAssetAssigned(asset);
    const includeMoveTaskDisabled =
      includeMoveTaskValue === 'disabled' && isAssetInActiveMoveTask(asset);

    return includeWaitingDisabled || includeAssignedDisabled || includeMoveTaskDisabled;
  };

  /**
   * Process and format assets for display in select component
   */
  const processedAssets = computed(() => {
    const searchQuery = toValue(search)?.trim().toLowerCase() || '';
    const showDisabledValue = toValue(showDisabled);

    const includeWaitingValue = toValue(includeWaitingAssignmentAssets);
    const includeAssignedValue = toValue(includeAssignedAssets);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const exclusions = [
      ...(includeWaitingValue ? [] : [shouldExcludeWaitingAssignmentAsset]),
      ...(includeAssignedValue ? [] : [shouldExcludeAssignedAsset]),
      ...(includeMoveTaskValue ? [] : [shouldExcludeAssetAssociatedToActiveMoveTask])
    ];

    const isExcludedBySomeFilter = (asset: YardAssetRow): boolean =>
      exclusions.some(exclude => exclude(asset));

    /**
     * Helper function to check if a string value matches the search query.
     * Handles null/undefined values and performs case-insensitive matching.
     */
    const matchesSearchString = (value: string | null | undefined): boolean => {
      if (!value || !searchQuery) {
        return false;
      }
      return value.toLowerCase().includes(searchQuery);
    };

    const matchesSearch = (asset: YardAssetRow): boolean => {
      if (!searchQuery) {
        return true;
      }

      return (
        matchesSearchString(asset.code) ||
        matchesSearchString(asset.spotArea?.name) ||
        matchesSearchString(asset.spot?.code)
      );
    };

    return assetRows.value
      .filter(asset => {
        if (isExcludedBySomeFilter(asset) || !matchesSearch(asset)) {
          return false;
        }

        if (!showDisabledValue && shouldDisableAsset(asset)) {
          return false;
        }

        return true;
      })
      .map(asset => ({
        assetId: asset.id,
        assetCode: asset.code,
        icon: isAssetAssigned(asset) ? 'mdi-truck-trailer' : 'mdi-clock-outline',
        isAssigned: isAssetAssigned(asset),
        spotAreaName: asset.spotArea?.name ?? null,
        spotCode: asset.spot?.code ?? null,
        spotId: asset.spot?.id ?? null,
        disabled: shouldDisableAsset(asset)
      }))
      .sort((a, b) => a.assetCode.localeCompare(b.assetCode));
  });

  return {
    assetsRef: processedAssets,
    isLoadingRef: isLoading,
    refresh
  };
}
